/* eslint-disable linebreak-style */
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash.debounce';
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Dialog,
  Grow,
  InputAdornment,
  DialogContent,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { ExpandMore, CancelOutlined } from '@material-ui/icons';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import Tooltip from 'material-ui/internal/Tooltip';
import Toggle from 'material-ui/Toggle';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { filter, every, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FlatButton } from '../../../../../../app/components/buttons';
import firebase from '../../../../../../firebase';
import ImageUpload from '../../../../../editor/internals/imageUpload';
import { addItemToCourses, updateItemFromCourse } from '../../api';
import './styles.css';
import { DialogHeader, ErrorWrapper, Info } from '../../Elements';

import { CourseContext } from '../../modules/courses/context/CourseContext';
import RichTextEditor from '../editor/RichTextEditor';
import { compressOversizedImagesInHtml } from '../editor/imageCompression';
import { useStyles } from './customStyles';
import { openSnackbar } from '../../../../../../app/modules/snackbar/actions';

// Local state + debounced context sync so typing doesn't re-render the whole modal every keystroke.
function DebouncedTextField({
  value,
  onChange,
  valueRef,
  fieldKey,
  debounceMs = 300,
  ...textFieldProps
}) {
  const [localValue, setLocalValue] = React.useState(value || '');
  const debouncedOnChangeRef = React.useRef(null);
  if (!debouncedOnChangeRef.current) {
    debouncedOnChangeRef.current = debounce(v => onChange(v), debounceMs);
  }
  const debouncedOnChange = debouncedOnChangeRef.current;

  React.useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = e => {
    const v = e.target.value;
    setLocalValue(v);
    if (valueRef) {
      const r = valueRef;
      if (fieldKey !== undefined) {
        const cur = r.current || {};
        r.current = { ...cur, [fieldKey]: v };
      } else {
        r.current = v;
      }
    }
    debouncedOnChange(v);
  };

  return (
    <TextField {...textFieldProps} value={localValue} onChange={handleChange} />
  );
}
DebouncedTextField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  valueRef: PropTypes.shape({ current: PropTypes.any }),
  fieldKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  debounceMs: PropTypes.number,
};

const AddCourse = props => {
  const {
    addCouresDialogOpen,
    addCourseModalScrollModuleIndex = null,
    addCourseModalScrollRestoreCount = 0,
    addCourseModalScrollTop = 0,
    onCoursesDialogOpen,
    onSetAddCourseModalScroll,
    onAddCourseModalScrollRestored,
    appname,
    onCourseItemChange,
    onResetCourseData,
    courseItem,
    currency,
    pages,
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const isUpdate = Object.keys(courseItem).length > 0;
  const accountId = useSelector(state => state.account.organisation.key);
  const isImageUploaded = useSelector(
    state => state.editor.internals.imageUpload.isImageUploaded,
  );
  const imagesUploaded = useSelector(
    state => state.editor.internals.imageUpload.imagesUploaded,
  );

  const getImgUrl = useCallback(
    (type, key) => {
      const firebasePathMap = {
        previewImage: `online_courses/${appname}/temp_images/previewImage/${accountId}`,
        previewVideo: `online_courses/${appname}/temp_images/previewVideo/${accountId}`,
        moduleMedia: `online_courses/${appname}/temp_images/moduleMedia/${key}/${accountId}`,
      };

      return firebasePathMap[type];
    },
    [appname, accountId],
  );

  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useMutation(
    isUpdate ? updateItemFromCourse : addItemToCourses,
  );

  const courseInfo = useContext(CourseContext);
  const {
    courseData,
    setCourseData,
    expandedModule,
    setExpandedModule,
    handleModuleChange,
    addModule,
    handleAccordionChange,
    resetCourseData,
    error,
    setError,
    isButtonDisabled,
    setIsButtonDisabled,
    stripeKeyError,
    setStripeKeyError,
  } = courseInfo;

  const moduleUpdatesRef = useRef({});
  const moduleContentRef = useRef({});
  const courseTitleRef = useRef('');
  const courseDescriptionRef = useRef('');
  const whatNextRef = useRef('');
  const moduleTitleRef = useRef({});
  const hasLoadedCourseRef = useRef(false);
  const [tooltipShown, setTooltipShown] = React.useState(false);

  const sortedRoutes = (() => {
    const copyRoutes = JSON.parse(JSON.stringify(cloneDeep(pages)));
    const filterRoutes = copyRoutes.filter(item => item.type !== 'Popup');
    return filterRoutes.sort((a, b) => a.title.localeCompare(b.title));
  })();

  const onMouseEnter = () => setTooltipShown(true);

  const onMouseLeave = () => setTooltipShown(false);

  useEffect(() => {
    const checkStripeKeyExists = async () => {
      setStripeKeyError(false);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_ONLINE_COURSE_API}/stripe-key-exists/${appname}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        const { status } = await response.json();

        setStripeKeyError(status);
      } catch (err) {
        setStripeKeyError(true);
      }
    };
    checkStripeKeyExists();
  }, []);

  // Load courseItem when dialog opens for edit. Never overwrite if user has added more lessons
  // (stops brief "remove then add back" when image preview is ready, e.g. after remount or effect re-run).
  useEffect(() => {
    if (!addCouresDialogOpen) {
      hasLoadedCourseRef.current = false;
      return;
    }
    if (isUpdate && courseItem) {
      const currentLen = Array.isArray(courseData.modules)
        ? courseData.modules.length
        : 0;
      let itemLen = 0;
      if (Array.isArray(courseItem.modules)) {
        itemLen = courseItem.modules.length;
      } else if (courseItem.modules && typeof courseItem.modules === 'object') {
        itemLen = Object.keys(courseItem.modules).filter(k => /^\d+$/.test(k))
          .length;
      }
      if (currentLen > itemLen) return;
      if (!hasLoadedCourseRef.current) {
        setCourseData(courseItem);
        hasLoadedCourseRef.current = true;
      }
    } else if (!isImageUploaded && !isUpdate) {
      resetCourseData();
    }
  }, [
    addCouresDialogOpen,
    isUpdate,
    courseItem,
    isImageUploaded,
    courseData.modules,
  ]);

  useEffect(() => {
    const getImageUrl = async () => {
      if (!isImageUploaded || !imagesUploaded) return;
      if (!Object.keys(imagesUploaded).length) return;

      try {
        const imagesKeys = Object.keys(imagesUploaded);
        const regularImages = imagesKeys.filter(
          key => !key.includes('moduleMedia_'),
        );

        if (regularImages.length > 0) {
          const updates = {};
          await Promise.all(
            regularImages.map(async item => {
              const url = getImgUrl(item);
              const imageRef = firebase.database().ref(url);
              const snapshot = await imageRef.once('value');
              const value = snapshot.val();
              if (value) updates[item] = value;
            }),
          );
          if (Object.keys(updates).length > 0) {
            setCourseData(prev => ({ ...prev, ...updates }));
          }
        }
      } catch (err) {
        console.log('error--->', err);
      }
    };

    getImageUrl();
  }, [isImageUploaded, imagesUploaded, getImgUrl]);

  // Separate effect for handling module media – fetch all then apply in one update to avoid "steps" UX
  useEffect(() => {
    const getModuleImageUrls = async () => {
      if (!isImageUploaded || !imagesUploaded) return;

      try {
        const imagesKeys = Object.keys(imagesUploaded);
        const moduleImages = imagesKeys.filter(key =>
          key.includes('moduleMedia_'),
        );

        if (moduleImages.length === 0) return;

        const pending = [];
        for (const item of moduleImages) {
          const urlPaths = item.split('_');
          const index = urlPaths.pop();
          if (moduleUpdatesRef.current[index]) continue;

          const formatItem = urlPaths.join('_');
          const url = getImgUrl(formatItem, index);
          const imageRef = firebase.database().ref(url);
          const snapshot = await imageRef.once('value');
          const value = snapshot.val();
          if (value) {
            moduleUpdatesRef.current[index] = true;
            const numIndex =
              typeof index === 'string' ? parseInt(index, 10) : index;
            pending.push({ numIndex, value });
          }
        }

        if (pending.length > 0) {
          setCourseData(prev => {
            let modules = prev.modules;
            if (
              !Array.isArray(modules) &&
              modules &&
              typeof modules === 'object'
            ) {
              const keys = Object.keys(modules)
                .filter(k => /^\d+$/.test(k))
                .sort((a, b) => Number(a) - Number(b));
              modules = keys.map(k => modules[k]);
            } else if (!Array.isArray(modules)) {
              modules = [];
            }
            let result = [...modules];
            for (const { numIndex, value } of pending) {
              while (result.length <= numIndex) {
                result.push({ title: '', media: '', content: '' });
              }
              const mod = result[numIndex] || {
                title: '',
                media: '',
                content: '',
              };
              const refTitle =
                moduleTitleRef.current[numIndex] !== undefined
                  ? moduleTitleRef.current[numIndex]
                  : moduleTitleRef.current[String(numIndex)];
              const refContent = moduleContentRef.current[numIndex];
              const nextTitle = mod.title || refTitle || '';
              const nextContent = mod.content || refContent || '';
              result = [...result];
              result[numIndex] = {
                ...mod,
                media: value,
                title: nextTitle,
                content: nextContent,
              };
            }
            return { ...prev, modules: result };
          });
        }
      } catch (err) {
        console.log('error handling module images:', err);
      }
    };

    getModuleImageUrls();
  }, [isImageUploaded, imagesUploaded, getImgUrl, setCourseData]);

  // Reset module updates ref when dialog closes
  useEffect(() => {
    if (!addCouresDialogOpen) {
      moduleUpdatesRef.current = {};
    }
  }, [addCouresDialogOpen]);

  const deleteUploadedImages = async () => {
    if (isImageUploaded && imagesUploaded) {
      const imagesKeys = Object.keys(imagesUploaded);
      await Promise.all(
        imagesKeys.map(async item => {
          let url = null;
          if (item.includes('moduleMedia_')) {
            const urlPaths = item.split('_');
            const index = urlPaths.pop();
            const formatItem = urlPaths.join('_');
            url = getImgUrl(formatItem, index);
          } else {
            url = getImgUrl(item);
          }
          await firebase
            .database()
            .ref(url)
            .set(null);
        }),
      );
    }
  };

  const handleClose = async () => {
    await deleteUploadedImages();
    resetCourseData();
    onResetCourseData();
  };

  const validateFields = () => {
    const errors = [];

    if (!courseData.previewImage) {
      errors.push(t('Course Image is required.'));
    }

    const description = courseDescriptionRef.current
      ? courseDescriptionRef.current
      : courseData.description;
    if (!description) {
      errors.push(t('Course Description is required.'));
    }

    if (courseData.isPaid && parseInt(courseData.price, 10) === 0) {
      errors.push(t('Price must be greater than 0.'));
    }

    const rawModules = courseData.modules;
    const modules = Array.isArray(rawModules)
      ? rawModules
      : rawModules && typeof rawModules === 'object'
      ? Object.keys(rawModules)
          .filter(k => /^\d+$/.test(k))
          .sort((a, b) => Number(a) - Number(b))
          .map(k => rawModules[k])
      : [];
    modules.forEach((module, i) => {
      let title = moduleTitleRef.current[i];
      if (title === undefined) title = moduleTitleRef.current[String(i)];
      if (title === undefined) title = module.title;
      const titleStr = typeof title === 'string' ? title.trim() : '';
      const stateTitleStr =
        typeof module.title === 'string' ? module.title.trim() : '';
      const hasTitle = titleStr || stateTitleStr;
      if (!hasTitle) {
        errors.push(
          t('Module {{number}} title is required.', { number: i + 1 }),
        );
      }

      let content =
        moduleContentRef.current[i] !== undefined
          ? moduleContentRef.current[i]
          : moduleContentRef.current[String(i)];
      if (content === undefined) content = module.content;
      const contentStr =
        typeof content === 'string'
          ? content.replace(/<[^>]*>/g, '').trim()
          : '';
      const stateContentStr =
        typeof module.content === 'string'
          ? module.content.replace(/<[^>]*>/g, '').trim()
          : '';
      const hasContent = contentStr || stateContentStr;
      if (!hasContent) {
        errors.push(
          t('Module {{number}} content is required.', { number: i + 1 }),
        );
      }
    });

    return errors;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      const errors = validateFields();
      if (errors.length > 0) {
        setError(errors);
        return;
      }
      if (isButtonDisabled) {
        return;
      }
      setError([]);

      const rawModules = courseData.modules.map((mod, i) => ({
        ...mod,
        title: moduleTitleRef.current[i]
          ? moduleTitleRef.current[i]
          : mod.title,
        content: moduleContentRef.current[i]
          ? moduleContentRef.current[i]
          : mod.content,
      }));
      const modulesWithLatestContent = await Promise.all(
        rawModules.map(async mod => ({
          ...mod,
          content: await compressOversizedImagesInHtml(
            typeof mod.content === 'string' ? mod.content : '',
          ),
        })),
      );
      const payload = {
        appname,
        courseData: {
          ...courseData,
          title: courseTitleRef.current
            ? courseTitleRef.current
            : courseData.title,
          description: courseDescriptionRef.current
            ? courseDescriptionRef.current
            : courseData.description,
          whatNext: whatNextRef.current
            ? whatNextRef.current
            : courseData.whatNext,
          modules: modulesWithLatestContent,
        },
      };
      setIsButtonDisabled(true);
      await mutateAsync(payload);

      queryClient.invalidateQueries('courseItems');
      await deleteUploadedImages();
      dispatch(
        openSnackbar(
          `${
            isUpdate ? t('Online Course updated') : t('Online Course added')
          } ${t('successfully.')}`,
        ),
      );
      onCoursesDialogOpen(false);
      onCourseItemChange({});
      setIsButtonDisabled(false);
      resetCourseData();
      onResetCourseData();
    } catch (err) {
      setIsButtonDisabled(false);
      dispatch(
        openSnackbar(`${t('Oops an error occured. Please try again later.')}`),
      );
    }
  };

  const dialogContentRef = React.useRef(null);
  const [, setScrollRetryAt4s] = React.useState(0);
  const [
    showScrollRestoreOverlay,
    setShowScrollRestoreOverlay,
  ] = React.useState(false);
  const prevModuleUploadCountRef = useRef(0);
  const overlayTimeoutRef = useRef(null);
  const contentUpdateScrollRef = useRef(null);
  // Per-module content at expand time: avoids re-rendering the editor when parent echoes back after paste.
  const stableInitialContentRef = useRef({});

  const isScrollable = useCallback(el => {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    return (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight
    );
  }, []);

  // Find the element that actually scrolls. MUI often puts scroll on a *parent* of DialogContent, not on the ref.
  // Save: walk up from click target. Restore: walk up from root then search descendants.
  const getScrollableElement = useCallback(
    fromElement => {
      const root = dialogContentRef.current;
      if (!root) return null;
      const walkUpToScrollable = startEl => {
        let el = startEl;
        while (el) {
          if (isScrollable(el)) return el;
          el = el.parentElement;
        }
        return null;
      };
      if (fromElement) {
        const found = walkUpToScrollable(fromElement);
        if (found && (found === root || found.contains(root))) return found;
      }
      if (isScrollable(root)) return root;
      let parent = root.parentElement;
      while (parent) {
        if (isScrollable(parent)) return parent;
        parent = parent.parentElement;
      }
      const search = node => {
        for (let i = 0; i < node.children.length; i += 1) {
          const child = node.children[i];
          if (isScrollable(child)) return child;
          const found = search(child);
          if (found) return found;
        }
        return null;
      };
      return search(root) || root;
    },
    [isScrollable],
  );

  // Clear editor content ref when dialog opens so we don't carry over from a previous session.
  useEffect(() => {
    if (addCouresDialogOpen) {
      moduleContentRef.current = {};
    }
  }, [addCouresDialogOpen]);

  const prevDialogOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = addCouresDialogOpen && !prevDialogOpenRef.current;
    prevDialogOpenRef.current = !!addCouresDialogOpen;
    if (!justOpened || !courseData) return;
    courseTitleRef.current = courseData.title != null ? courseData.title : '';
    courseDescriptionRef.current =
      courseData.description != null ? courseData.description : '';
    whatNextRef.current =
      courseData.whatNext != null ? courseData.whatNext : '';
    if (courseData.modules) {
      const next = {};
      courseData.modules.forEach((mod, i) => {
        next[i] = mod.title != null ? mod.title : '';
      });
      moduleTitleRef.current = next;
    }
  }, [addCouresDialogOpen, courseData]);

  // When user expands a different module, set stable content for that module so editor gets it once.
  // Prefer ref (latest typed/pasted) over state so we show current content when switching back.
  useEffect(() => {
    const mod = courseData.modules && courseData.modules[expandedModule];
    if (mod) {
      const latest = moduleContentRef.current[expandedModule]
        ? moduleContentRef.current[expandedModule]
        : mod.content;
      stableInitialContentRef.current[expandedModule] =
        latest != null ? latest : '';
    }
  }, [expandedModule]);

  // Save scroll when user interacts with any ImageUpload. Defer to next tick so we don't delay the dialog opening.
  // Flush this module's content from ref to context so it isn't lost when the image upload completes.
  React.useEffect(() => {
    const handleGlobalClick = e => {
      const imageUploadElement = e.target.closest('[data-image-upload]');
      if (
        !imageUploadElement ||
        !dialogContentRef.current ||
        typeof onSetAddCourseModalScroll !== 'function'
      ) {
        return;
      }
      const target = e.target;
      const moduleEl = imageUploadElement.closest('[data-module-index]');
      const moduleIndex =
        moduleEl !== null
          ? parseInt(moduleEl.getAttribute('data-module-index'), 10)
          : null;
      const safeIndex =
        Number.isFinite(moduleIndex) && moduleIndex >= 0 ? moduleIndex : null;

      setTimeout(() => {
        if (!dialogContentRef.current) return;
        const scrollEl = getScrollableElement(target);
        const scrollTop =
          scrollEl && typeof scrollEl.scrollTop === 'number'
            ? scrollEl.scrollTop
            : 0;
        onSetAddCourseModalScroll(scrollTop, 6, safeIndex);
        // Flush this lesson's title and content to context in one update so they persist after image upload
        if (safeIndex != null && typeof setCourseData === 'function') {
          const title =
            moduleTitleRef.current[safeIndex] !== undefined
              ? moduleTitleRef.current[safeIndex]
              : moduleTitleRef.current[String(safeIndex)];
          const content = moduleContentRef.current[safeIndex];
          setCourseData(prev => {
            const mod = prev.modules[safeIndex];
            if (!mod) return prev;
            return {
              ...prev,
              modules: prev.modules.map((m, i) =>
                i !== safeIndex
                  ? m
                  : {
                      ...m,
                      title: title !== undefined ? title : m.title,
                      content: content !== undefined ? content : m.content,
                    },
              ),
            };
          });
        }
      }, 0);
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [onSetAddCourseModalScroll, getScrollableElement, setCourseData]);

  // Show overlay when a module image upload completes; hide after 2s (don't clear this timeout in cleanup or it never fires).
  useEffect(() => {
    const keys = Object.keys(imagesUploaded || {});
    const moduleCount = keys.filter(k => k.indexOf('moduleMedia_') !== -1)
      .length;
    const hadPending =
      addCourseModalScrollRestoreCount > 0 && addCourseModalScrollTop > 0;
    if (moduleCount > prevModuleUploadCountRef.current && hadPending) {
      prevModuleUploadCountRef.current = moduleCount;
      setShowScrollRestoreOverlay(true);
      if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = setTimeout(() => {
        setShowScrollRestoreOverlay(false);
        overlayTimeoutRef.current = null;
      }, 1000);
    } else {
      prevModuleUploadCountRef.current = moduleCount;
    }
  }, [
    imagesUploaded,
    addCourseModalScrollRestoreCount,
    addCourseModalScrollTop,
  ]);

  useEffect(
    () => () => {
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
        overlayTimeoutRef.current = null;
      }
    },
    [],
  );

  // Hide overlay when restore is done or dialog closes. Reset ref when dialog closes.
  useEffect(() => {
    if (!addCouresDialogOpen) {
      prevModuleUploadCountRef.current = 0;
    }
    if (
      addCourseModalScrollRestoreCount === 0 ||
      addCourseModalScrollTop === 0 ||
      !addCouresDialogOpen
    ) {
      setShowScrollRestoreOverlay(false);
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
        overlayTimeoutRef.current = null;
      }
    }
  }, [
    addCourseModalScrollRestoreCount,
    addCourseModalScrollTop,
    addCouresDialogOpen,
  ]);

  // Restore modal scroll after module content editor update (paste/type) so accordion doesn't jump to start.
  useLayoutEffect(() => {
    const saved = contentUpdateScrollRef.current;
    if (saved == null || !dialogContentRef.current) return;
    contentUpdateScrollRef.current = null;
    const scrollEl = getScrollableElement(dialogContentRef.current);
    if (scrollEl) scrollEl.scrollTop = saved;
  });

  // Restore scroll from Redux (scrollTop). Only decrement when we actually fix the scroll.
  useLayoutEffect(() => {
    if (
      addCourseModalScrollRestoreCount <= 0 ||
      addCourseModalScrollTop <= 0 ||
      !dialogContentRef.current ||
      typeof onAddCourseModalScrollRestored !== 'function'
    ) {
      return undefined;
    }
    const targetScroll = addCourseModalScrollTop;
    const scrollEl = getScrollableElement(dialogContentRef.current);
    if (!scrollEl || scrollEl.scrollTop === targetScroll) {
      return undefined;
    }
    scrollEl.scrollTop = targetScroll;
    onAddCourseModalScrollRestored();
    const applyScroll = () => {
      const r = dialogContentRef.current;
      if (!r) return;
      const el = getScrollableElement(r);
      if (el && el.scrollTop !== targetScroll) el.scrollTop = targetScroll;
    };
    requestAnimationFrame(applyScroll);
    const t0 = setTimeout(applyScroll, 0);
    const t4 = setTimeout(() => setScrollRetryAt4s(n => n + 1), 4000);
    return () => {
      clearTimeout(t0);
      clearTimeout(t4);
    };
  });

  return (
    <Dialog
      data-testid="add-course"
      open={addCouresDialogOpen}
      autoScrollBodyContent
      disableEnforceFocus
      scroll="paper"
      maxWidth="md"
      fullWidth
      keepMounted
    >
      <div className="course-modal-content">
        <DialogHeader>
          <div />
          <h6>
            {isUpdate ? (
              <Trans>Edit Course</Trans>
            ) : (
              <Trans>Add a Course</Trans>
            )}
          </h6>
          <div>
            <CancelOutlined onClick={() => handleClose()} />
          </div>
        </DialogHeader>
        <DialogContent
          ref={dialogContentRef}
          style={{ width: '100%', padding: '0px', position: 'relative' }}
        >
          {showScrollRestoreOverlay && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              bgcolor="transparent"
              sx={{ cursor: 'not-allowed' }}
              zIndex={10}
            >
              <CircularProgress size={40} />
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  <Trans>Updating...</Trans>
                </Typography>
              </Box>
            </Box>
          )}
          <form onSubmit={handleSubmit} data-testid="form">
            <fieldset>
              <Grid container spacing={3} width={'92%'}>
                <Grid item md={6}>
                  <div>
                    <Trans>Course Image</Trans>
                  </div>
                  <Box data-image-upload>
                    <ImageUpload
                      targetId="previewImage"
                      src={courseData.previewImage}
                      label={t('Image')}
                      caption={t(
                        'This image will be displayed for this course.',
                      )}
                      firebaseUrl={getImgUrl('previewImage')}
                      title={t('Course Image')}
                      tooltip={t('Minimum image size 240x240 pixels')}
                      showCropAreaSizes
                      {...(isUpdate && {
                        noDelete: true,
                      })}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Grid container spacing={3} width={'92%'}>
                <Grid item md={6}>
                  <div>
                    <Trans>Course Video Preview</Trans>
                  </div>
                  <Box data-image-upload>
                    <ImageUpload
                      targetId="previewVideo"
                      src={courseData.previewVideo}
                      label={t('Video')}
                      caption={t(
                        'This video will be the preview video for this course.',
                      )}
                      firebaseUrl={getImgUrl('previewVideo')}
                      title={t('Course Video Preview')}
                      tooltip={t('Maximum video size is 300MB')}
                      showCropAreaSizes
                      isVideoOnlyAllowed
                      {...(isUpdate && {
                        noDelete: true,
                      })}
                    />
                  </Box>
                </Grid>
              </Grid>

              <div className="field-wrapper">
                <DebouncedTextField
                  type="text"
                  id="title"
                  label={t('Course Title')}
                  value={courseData.title}
                  valueRef={courseTitleRef}
                  required
                  fullWidth
                  margin="normal"
                  onChange={value =>
                    setCourseData(prev => ({ ...prev, title: value }))
                  }
                  onFocus={() =>
                    onSetAddCourseModalScroll &&
                    onSetAddCourseModalScroll(0, 0, null)
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </div>

              <div className="field-wrapper">
                <DebouncedTextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('Course Description')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={courseData.description}
                  valueRef={courseDescriptionRef}
                  onChange={value =>
                    setCourseData(prev => ({ ...prev, description: value }))
                  }
                  onFocus={() =>
                    onSetAddCourseModalScroll &&
                    onSetAddCourseModalScroll(0, 0, null)
                  }
                  required
                />
              </div>

              {/* Modules Section */}
              <div className="field-wrapper">
                <Grid item xs={12} style={{ margin: '20px 0' }}>
                  <Grid className="addMoreWrapper">
                    <Typography variant="h6" gutterBottom>
                      <Trans>Course Modules</Trans>
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={addModule}
                      className={classes.addButton}
                    >
                      + <Trans>Add Module</Trans>
                    </Button>
                  </Grid>
                  {courseData.modules.map((module, index) => (
                    <Accordion
                      key={index}
                      data-module-index={index}
                      expanded={expandedModule === index}
                      onChange={handleAccordionChange(index)}
                      elevation={2}
                      sx={{ mb: 2 }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls={`module${index}-content`}
                        id={`module${index}-header`}
                      >
                        <Typography>
                          <Trans>Module</Trans> - {index + 1}
                          {module.title && `: ${module.title}`}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box p={2}>
                          <Grid container spacing={3}>
                            <Grid item md={6}>
                              <div>
                                <Trans>Module Video/Image</Trans>
                              </div>
                              <Box key={index} mb={3} data-image-upload>
                                <ImageUpload
                                  targetId={`moduleMedia_${index}`}
                                  src={module.media}
                                  label={t('Module Video/Image')}
                                  caption={t(
                                    'This image will be video/image for this course',
                                  )}
                                  firebaseUrl={getImgUrl('moduleMedia', index)}
                                  title={t('Module Video/Image')}
                                  showCropAreaSizes
                                  isVideoAllowed
                                  {...(isUpdate && {
                                    noDelete: true,
                                  })}
                                />
                              </Box>
                            </Grid>
                          </Grid>

                          <DebouncedTextField
                            fullWidth
                            label={t('Module Title')}
                            value={module.title}
                            valueRef={moduleTitleRef}
                            fieldKey={index}
                            onChange={value =>
                              handleModuleChange(index, 'title', value)
                            }
                            margin="normal"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />

                          <Box my={2}>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              className={classes.subTitle}
                            >
                              <Trans>Module Content</Trans>
                            </Typography>
                            <RichTextEditor
                              initialValue={
                                moduleContentRef.current[index]
                                  ? moduleContentRef.current[index]
                                  : stableInitialContentRef.current[index]
                                  ? stableInitialContentRef.current[index]
                                  : module.content
                              }
                              onChange={content => {
                                moduleContentRef.current[index] = content;
                              }}
                            />
                          </Box>

                          {index > 0 && (
                            <Box mt={2}>
                              <Button
                                variant="outlined"
                                color="secondary"
                                className={classes.removeButton}
                                onClick={() => {
                                  const updatedModules = [
                                    ...courseData.modules,
                                  ];
                                  updatedModules.splice(index, 1);

                                  const nextContent = {};
                                  Object.keys(moduleContentRef.current).forEach(
                                    k => {
                                      const i = parseInt(k, 10);
                                      if (Number.isNaN(i)) return;
                                      if (i < index)
                                        nextContent[i] =
                                          moduleContentRef.current[k];
                                      else if (i > index)
                                        nextContent[i - 1] =
                                          moduleContentRef.current[k];
                                    },
                                  );
                                  moduleContentRef.current = nextContent;
                                  const nextTitles = {};
                                  Object.keys(moduleTitleRef.current).forEach(
                                    k => {
                                      const i = parseInt(k, 10);
                                      if (Number.isNaN(i)) return;
                                      if (i < index)
                                        nextTitles[i] =
                                          moduleTitleRef.current[k];
                                      else if (i > index)
                                        nextTitles[i - 1] =
                                          moduleTitleRef.current[k];
                                    },
                                  );
                                  moduleTitleRef.current = nextTitles;

                                  setCourseData(prev => ({
                                    ...prev,
                                    modules: updatedModules,
                                  }));
                                  // Adjust expanded module index after deletion
                                  if (expandedModule >= index) {
                                    setExpandedModule(
                                      Math.max(0, expandedModule - 1),
                                    );
                                  }
                                }}
                              >
                                <Trans>Remove Module</Trans>
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Grid>
              </div>

              <div className="field-wrapper">
                <DebouncedTextField
                  type="text"
                  id="whatNext"
                  label={t("What's Next After Completion")}
                  required
                  fullWidth
                  margin="normal"
                  value={courseData.whatNext}
                  valueRef={whatNextRef}
                  multiline
                  InputLabelProps={{
                    shrink: true,
                  }}
                  placeholder={t('Content to show after course completion.')}
                  rows={4}
                  onChange={value =>
                    setCourseData(prev => ({ ...prev, whatNext: value }))
                  }
                  onFocus={() =>
                    onSetAddCourseModalScroll &&
                    onSetAddCourseModalScroll(0, 0, null)
                  }
                />
              </div>

              <div className="field-wrapper">
                <TextField
                  fullWidth
                  select
                  label={t('Link to App Page')}
                  value={courseData.nextPageLink}
                  onChange={e => {
                    const value = e.target.value;
                    setCourseData(prev => ({
                      ...prev,
                      nextPageLink: value,
                    }));
                  }}
                  SelectProps={{
                    native: true,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                >
                  <option value="">{t('Select a page')}</option>
                  {sortedRoutes.map((item, index) => (
                    <option key={index} value={item.path}>
                      {item.title}
                    </option>
                  ))}
                </TextField>
              </div>

              <div className="field-wrapper custom-wrapper">
                <Toggle
                  onToggle={(v, b) => {
                    const value = b;
                    setCourseData(prev => ({
                      ...prev,
                      isActive: value,
                    }));
                  }}
                  toggled={courseData.isActive}
                  label={
                    <span>
                      <Trans>Enable Course</Trans>
                    </span>
                  }
                  className={classes.customToggle}
                />
              </div>

              <div className="field-wrapper custom-wrapper">
                <Toggle
                  onToggle={(v, b) => {
                    const value = b;
                    setCourseData(prev => ({
                      ...prev,
                      isPaid: value,
                    }));
                  }}
                  toggled={courseData.isPaid}
                  label={t('Enable Payment')}
                />
              </div>
              <Grow in={stripeKeyError && courseData.isPaid} unmountOnExit>
                <div>
                  <p className="subscription-error-msg">
                    <Trans>Add stripe keys to add paid courses.</Trans>
                  </p>
                  <p className="subscription-link">
                    <Link
                      to={{
                        pathname: `/${appname}/ecommerce/online-courses/settings`,
                        hash: '#user-app-subscriptions',
                      }}
                    >
                      <Trans>Click here</Trans>
                    </Link>{' '}
                    <Trans>to add keys</Trans>
                  </p>
                </div>
              </Grow>

              <Grow in={courseData.isPaid} unmountOnExit>
                <div className="field-wrapper custom-wrapper">
                  <TextField
                    type="number"
                    label={t('Price')}
                    value={courseData.price}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {
                            new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency,
                            })
                              .formatToParts(0)
                              .find(part => part.type === 'currency').value
                          }
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      min: 1,
                      step: 0.01,
                    }}
                    onChange={e => {
                      const value = parseFloat(
                        parseFloat(e.target.value).toFixed(2),
                      );
                      setCourseData(prev => ({
                        ...prev,
                        price: value,
                      }));
                    }}
                  />
                  ;
                </div>
              </Grow>
              <div className="field-wrapper custom-wrapper">
                <Info onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                  <InfoIcon color="primary" fontSize="small" />{' '}
                  <Tooltip
                    label={t(
                      "Users have access to the course for a set number of days from their enrollment date. After this period, the course will be marked as 'Expired'.",
                    )}
                    show={tooltipShown}
                    style={{ fontSize: 11 }}
                  />
                  <span />
                </Info>
                <Toggle
                  onToggle={(v, b) => {
                    const value = b;
                    setCourseData(prev => ({
                      ...prev,
                      courseDuration: value,
                    }));
                  }}
                  toggled={courseData.courseDuration}
                  label={
                    <>
                      <span> {t('Enable Course Duration')}</span>
                    </>
                  }
                />
              </div>

              <Grow in={courseData.courseDuration} unmountOnExit>
                <div className="field-wrapper custom-wrapper">
                  <TextField
                    type="number"
                    label={t('Duration')}
                    value={courseData.durationInDays}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Trans>In days</Trans>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      min: 1,
                      step: 1,
                    }}
                    onChange={e => {
                      const value = parseInt(e.target.value, 10);
                      setCourseData(prev => ({
                        ...prev,
                        durationInDays: value,
                      }));
                    }}
                  />
                  ;
                </div>
              </Grow>

              <div>
                <FlatButton
                  label={
                    isLoading
                      ? t('SAVING...')
                      : isUpdate
                      ? t('Update')
                      : t('ADD')
                  }
                  type="submit"
                  primary
                />
              </div>

              {error && error.length > 0 && (
                <ErrorWrapper>
                  {error.map(e => (
                    <p key={e}>{e}</p>
                  ))}
                </ErrorWrapper>
              )}
            </fieldset>
          </form>
        </DialogContent>
      </div>
    </Dialog>
  );
};

AddCourse.propTypes = {
  appname: PropTypes.string.isRequired,
  addCouresDialogOpen: PropTypes.bool.isRequired,
  addCourseModalScrollModuleIndex: PropTypes.number,
  addCourseModalScrollRestoreCount: PropTypes.number,
  addCourseModalScrollTop: PropTypes.number,
  onCoursesDialogOpen: PropTypes.func.isRequired,
  onSetAddCourseModalScroll: PropTypes.func,
  onAddCourseModalScrollRestored: PropTypes.func,
  onResetCourseData: PropTypes.func.isRequired,
  courseItem: PropTypes.object.isRequired,
  onCourseItemChange: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  pages: PropTypes.array.isRequired,
};

AddCourse.defaultProps = {
  addCourseModalScrollModuleIndex: null,
  addCourseModalScrollRestoreCount: 0,
  addCourseModalScrollTop: 0,
  onSetAddCourseModalScroll: undefined,
  onAddCourseModalScrollRestored: undefined,
};

export default React.memo(AddCourse);
